import { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ELEMENT_META, WORK_META } from "@/data/breedingPals";
import { PALS_BY_INDEX } from "@/lib/breeding";
import { PARTNER_SKILLS } from "@/data/partnerSkills";
import { ALPHA_LOC } from "@/data/mapPois";
import { PalAvatar, ElementBadges } from "@/components/PalAvatar";
import { usePalCompare } from "@/components/PalCompareModal";
import {
  X, Heart, Sword, Shield, Gauge, Moon, Sun, Coins, Star, Dna, Sparkles, Scale, MapPin,
  Flame, Droplets, Sprout, Zap, Hammer, Hand, Axe, Pickaxe, FlaskConical, Snowflake, Truck, Wheat,
} from "lucide-react";

const WORK_ICONS = {
  kindling: Flame, watering: Droplets, planting: Sprout, electricity: Zap,
  handiwork: Hammer, gathering: Hand, lumbering: Axe, mining: Pickaxe,
  medicine: FlaskConical, cooling: Snowflake, transport: Truck, farming: Wheat,
};

const PalDetailContext = createContext(() => {});
export const usePalDetail = () => useContext(PalDetailContext);

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-black/20 px-3 py-2">
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">{label}</div>
        <div className="text-sm font-bold">{value}</div>
      </div>
    </div>
  );
}

function PalDetailModal({ idx, onClose }) {
  const openCompare = usePalCompare();
  const navigate = useNavigate();
  if (idx == null) return null;
  const p = PALS_BY_INDEX[idx];
  if (!p) return null;
  const d = p.d || {};
  const rarity = p.rarity || 0;
  const stars = Math.max(1, Math.min(5, Math.ceil(rarity / 2)));
  const work = d.work || {};
  const skill = PARTNER_SKILLS[p.iname];
  const isAlpha = !!ALPHA_LOC[idx];

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="pal-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-[hsl(var(--surface))] shadow-2xl overflow-hidden fade-up max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="relative p-5 border-b border-border bg-black/25">
          <button onClick={onClose} data-testid="pal-detail-close" className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <PalAvatar pal={p} size={72} showElement />
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/70">
                N°{p.dex >= 10000 ? "?" : p.dex}{p.variant ? " · Variante" : ""}
              </div>
              <div className="font-head text-2xl font-bold leading-tight">{p.fr}</div>
              {p.fr !== p.name && <div className="text-xs text-muted-foreground">{p.name}</div>}
              <div className="mt-1.5 flex items-center gap-2">
                <ElementBadges elements={p.elements} />
                <span className="inline-flex items-center gap-0.5 text-amber-400">
                  {Array.from({ length: stars }).map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={Heart} label="PV" value={d.hp ?? "–"} color="#22C55E" />
            <Stat icon={Sword} label="Attaque" value={d.atk ?? "–"} color="#F87171" />
            <Stat icon={Shield} label="Défense" value={d.def ?? "–"} color="#60A5FA" />
            <Stat icon={Gauge} label="Vitesse (monte)" value={d.ride ?? "–"} color="#A78BFA" />
            <Stat icon={d.noct ? Moon : Sun} label="Activité" value={d.noct ? "Nocturne" : "Diurne"} color="#FBBF24" />
            <Stat icon={Coins} label="Prix" value={d.price ?? "–"} color="#FCD34D" />
          </div>

          {skill && (
            <div data-testid="partner-skill">
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">Capacité partenaire</div>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-bold text-primary">{skill.title || "Capacité partenaire"}</span>
                </div>
                {skill.desc && skill.desc !== skill.title && <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed">{skill.desc}</p>}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">Compétences de travail</div>
            {Object.keys(work).length === 0 ? (
              <div className="text-sm text-muted-foreground">Aucune compétence de travail.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(work).map(([k, lvl]) => {
                  const Icon = WORK_ICONS[k] || Hand;
                  return (
                    <div key={k} className="flex items-center gap-2 rounded-lg border border-border bg-black/20 px-3 py-2" data-testid={`work-${k}`}>
                      <Icon className="w-4 h-4 text-primary shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{WORK_META[k]}</div>
                      </div>
                      <span className="text-xs font-bold text-primary">Niv {lvl}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>Taille : <span className="text-foreground font-semibold">{d.size || "?"}</span></span>
            <span>Niveau sauvage : <span className="text-foreground font-semibold">{d.wmin ?? "?"}–{d.wmax ?? "?"}</span></span>
            <span className="inline-flex items-center gap-1"><Dna className="w-3.5 h-3.5" /> Puissance breeding : <span className="text-foreground font-semibold">{p.power}</span></span>
          </div>

          <button
            data-testid="pal-detail-compare"
            onClick={() => { const i = idx; onClose(); openCompare(i, null); }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 text-primary px-4 py-2.5 text-sm font-semibold hover:bg-primary/20 transition-colors"
          >
            <Scale className="w-4 h-4" /> Comparer ce Pal
          </button>
          {isAlpha && (
            <button
              data-testid="pal-detail-locate"
              onClick={() => { onClose(); navigate(`/map?focus=alpha:${idx}`); }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-2.5 text-sm font-semibold hover:bg-amber-500/20 transition-colors"
            >
              <MapPin className="w-4 h-4" /> Localiser sur la carte (Alpha)
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PalDetailProvider({ children }) {
  const [idx, setIdx] = useState(null);
  const open = useCallback((i) => setIdx(i), []);
  return (
    <PalDetailContext.Provider value={open}>
      {children}
      <PalDetailModal idx={idx} onClose={() => setIdx(null)} />
    </PalDetailContext.Provider>
  );
}
